/**
 * All the wedding's own details, in one place. Edit this file and the site
 * updates.
 *
 * It is deliberately not in the database or the API: none of it varies per
 * guest, so keeping it as typed data means no admin screen to build and no
 * migration to run when the venue changes its mind about parking.
 *
 * Anything still marked PLACEHOLDER is made up and needs your words.
 */

/** Who a point in the running order is relevant to. */
export type Audience =
  /** Day guests only - it has happened before the evening guests arrive. */
  | 'day'
  /** Everyone who is coming, whichever invite they have. */
  | 'all'

/** One thing happening in a time slot. */
export interface TimelinePoint {
  title: string
  detail?: string
}

/**
 * A moment in the running order, and everything that happens at it.
 *
 * The time and the audience belong to the slot rather than to each point: a
 * slot is a single moment on the clock, so anything listed under it is
 * happening then, for the same set of guests.
 */
export interface TimelineSlot {
  time: string
  audience: Audience
  points: readonly TimelinePoint[]
}

export interface InfoSection {
  heading: string
  body: string[]
}

export interface FaqItem {
  question: string
  answer: string
}

/*
 * The deadlines, hoisted out of the object below.
 *
 * They are consts rather than only fields because the FAQ answers are prose and
 * need to quote them, and an object literal cannot refer to itself as it is being
 * built. Every mention of either date - the reply form, the food section, the
 * questions - resolves to one of these, so changing a deadline is one edit.
 */

/**
 * When guests need to have answered.
 *
 * The first of the month, deliberately: a clean date to hold people to, and it
 * leaves the rest of the summer to settle the numbers with the venue.
 *
 * A deadline, not a target: the FAQ answers below promise nothing after it.
 * Replying earlier genuinely helps, which is what `rsvpEarly` says - but "as soon
 * as you can" is a request, and this is the date.
 */
const RSVP_BY = 'Thursday 1 July 2027'

/**
 * When the kitchen needs to know who cannot eat what.
 *
 * The same day as the reply deadline, and deliberately not a date of its own:
 * two dates to remember is worse for a guest than one, and both sets of numbers
 * are wanted by the same planning.
 *
 * Written as a reference rather than the same string twice, so the two cannot
 * drift apart while they are meant to match. It keeps its own name because the
 * two answer different questions in different places - the food section quotes
 * `dietaryBy`, the reply form `rsvpBy` - so one could be moved without turning
 * every mention of the other into a lie.
 */
const DIETARY_BY = RSVP_BY

export const weddingInfo = {
  /** Shown in the header and the browser tab. */
  couple: 'Tom & Jess',

  /** Human-readable; not parsed, so write it however you like. */
  date: 'Saturday 25 September 2027',
  dateShort: '25.09.2027',

  /**
   * The same day in a form code can read, for the countdown. Local midnight, so
   * "days to go" matches what a guest would count on a calendar rather than
   * shifting by a timezone.
   */
  isoDate: '2027-09-25',

  venue: {
    name: 'De Vere Horsley Estate',
    address: 'Ockham Rd S, East Horsley, Leatherhead KT24 6DT',
    /** Leave as null to hide the map link. */
    mapsUrl:
      'https://www.google.com/maps/place/De+Vere+Horsley+Estate/@51.2650049,-0.4319689,949m/data=!3m2!1e3!4b1!4m9!3m8!1s0x4875de83cebfa7d7:0x5c67ac743333475b!5m2!4m1!1i2!8m2!3d51.2650016!4d-0.429394!16s%2Fg%2F1x6q16yh?entry=ttu&g_ep=EgoyMDI2MDcyOS4wIKXMDSoASAFQAw%3D%3D',
  },

  /** One line under the couple's names on the invite. */
  intro:
    'We would be so happy to have you with us. Please let us know below whether you can make it.',

  /** When guests need to have said whether they are coming. */
  rsvpBy: RSVP_BY,

  /**
   * The nudge to reply sooner than the deadline.
   *
   * Kept separate from the deadline rather than folded into it, because the two
   * are different kinds of statement: one is a date we hold people to, the other
   * a favour we are asking. Blurring them would soften the date, which is exactly
   * what the rest of this copy avoids.
   */
  rsvpEarly: 'The sooner we know, the easier everything else is to plan.',

  /** When the kitchen needs everyone's dietary requirements. */
  dietaryBy: DIETARY_BY,

  /**
   * Staying the night in Horsley Towers, the main venue.
   *
   * The rooms themselves are not listed here: they come from the server, which
   * is the only thing that knows how many are left. This is the wording around
   * them, and the alternative for when they run out.
   */
  rooms: {
    intro:
      'There are 28 rooms in the manor house itself, on a first come, first served basis. One room per invitation, for the night of the wedding.',
    /*
     * How the room is paid for.
     *
     * Nothing in this site takes payment, and reserving here is not paying - so
     * this has to be said next to the prices, or a guest could reasonably think
     * the booking settled it.
     */
    payment:
      'Rooms in the manor house are paid for on the way out in the morning, or to us if that is easier. Reserving one here does not pay for it.',
    /*
     * The ways to settle up, shown on a reservation we have not marked as paid.
     *
     * Deliberately three options rather than one: some people will want it dealt
     * with now, and some would rather hand over cash on the day. Nothing is
     * charged automatically either way.
     */
    payOptions: {
      intro: 'Whenever suits you:',
      cash: 'Cash to us on the day',
      onLeaving: 'At the venue when you check out in the morning',
      online: 'Or pay now',
      /**
       * The monzo.me handle to pay. The amount and the invite code are added per
       * guest, so the payment arrives already labelled with who it is from.
       */
      monzoHandle: 'thomaspeterwright',
      /**
       * Whose account the money lands in, and what paying commits them to.
       *
       * Whose account is said outright, because a link that asks for £190 and
       * does not say where it goes is exactly what a guest should be wary of
       * clicking. Naming Tom's own account is the whole reassurance.
       *
       * The change-room caveat is here rather than only in `paidWarning`, which
       * a guest reading this has not seen: that one only appears once a room is
       * marked paid, so at this point nobody has been told what paying ties them
       * to. Better said before the money moves than after.
       */
      onlineNote:
        "Monzo is Tom's own bank account - the link opens with the amount and your code already filled in, so we can see who it came from. We will tick it off at our end. If you change room after paying, any refund or extra payment has to be sorted out between us, so do just contact Tom.",
      /*
       * Changing or cancelling a room that has been paid for.
       *
       * The payment follows the money rather than the room, so it survives a move
       * between two rooms at the same price - the Junior Suites and the Family
       * Rooms are both £190, and £190 handed over still covers either.
       *
       * What it cannot survive is a different price or a cancellation: one owes a
       * difference and the other a refund, and neither is something this site can
       * do. So the only honest answer there is to say who to speak to.
       */
      paidWarning:
        'You have paid for this room. Moving to a room at the same price is fine - the payment comes with you. If you move to a different price or give the room up, please speak to Tom so we can sort the money out, as the website cannot move it for you.',
      paidAfterChange:
        'Because that room was already paid for, please speak to Tom about the difference or a refund. Nothing has been moved automatically.',
    },
    /*
     * Access, said before the rooms rather than after them.
     *
     * Somebody who cannot manage stairs needs this while they are choosing, not
     * once they have reserved - so it sits above the list, and it names the
     * alternative in the same breath rather than only ruling the manor house out.
     */
    access:
      'The manor house rooms are all upstairs and do not have disabled access. The rooms at Horsley Place do, so please book there instead if stairs are a problem.',
    checkIn: 'Check in from 2:00pm on Saturday 25 September',
    checkOut: 'Check out by 10:00am on Sunday 26 September',
    /** Where to send people once the towers are full, or when stairs rule them out. */
    alternative: {
      name: 'Horsley Place',
      body: 'The hotel on the grounds is a two minute walk away and has another 120 rooms, with disabled access. Book those directly with De Vere rather than through us.',
      url: 'https://www.devere.co.uk/horsley-estate/',
    },
    /** Shown in place of the room list before anybody has said they are coming. */
    beforeReplying: 'Tell us you are coming and you can reserve one of these here.',
    /**
     * Shown to somebody who is coming but has arrived to find nothing left.
     *
     * Without it the page is four cards reading "Fully booked" and no word on
     * what to do about it - true, but not an answer.
     */
    allTaken:
      'The manor house rooms have all gone, we are afraid. Horsley Place, below, is a two minute walk and still has rooms.',
  },

  /**
   * The running order. `audience: 'day'` marks everything that happens before
   * the evening guests arrive, so an evening-only invite can be shown the part
   * that actually applies to them.
   *
   * A slot can list several points, for when more than one thing happens at the
   * same time:
   *
   *   {
   *     time: '7:00pm',
   *     audience: 'all',
   *     points: [
   *       { title: 'Evening guests arrive' },
   *       { title: 'BBQ', detail: 'Help yourselves to the spread' },
   *     ],
   *   }
   */
  timeline: [
    {
      time: '11:00am',
      audience: 'day',
      points: [{ title: 'Day guests arrive' }],
    },
    {
      time: '12:00pm',
      audience: 'day',
      points: [{ title: 'Ceremony', detail: 'The Great Hall' }],
    },
    {
      time: '12:30pm',
      audience: 'day',
      points: [
        {
          title: 'Post ceremony',
          detail: 'Your chance to mingle and take photos while we get the tables ready',
        },
      ],
    },
    {
      time: '1:30pm',
      audience: 'day',
      points: [{ title: 'Wedding breakfast', detail: 'Eating, drinking and speeches' }],
    },
    {
      time: '4:30pm',
      audience: 'day',
      points: [
        {
          title: 'Let the food go down',
          detail: 'Have a little chill out time before we kick off round two',
        },
      ],
    },
    {
      time: '5pm',
      audience: 'all',
      points: [
        {
          title: 'Evening guests arrive',
          detail:
            'Welcome the remaining guests and prepare for the evening festivities with a BBQ spread',
        },
      ],
    },
    {
      time: '5:30pm',
      audience: 'all',
      points: [{ title: 'Cut the cake', detail: '...' }],
    },
    {
      time: '6:00pm',
      audience: 'all',
      points: [
        { title: 'First dance', detail: "Don't be afraid to join in" },
        { title: 'Party time', detail: 'Welcome the band to the stage' },
      ],
    },
    {
      time: '8:00pm',
      audience: 'all',
      points: [
        {
          title: 'DJ',
          detail: 'End the night with a DJ playing all the classics',
        },
      ],
    },
    {
      time: '12:00am',
      audience: 'all',
      points: [{ title: 'Carriages' }],
    },
  ] satisfies TimelineSlot[],

  sections: [
    {
      heading: 'Getting there',
      body: [
        'Find the estate using the address or the Google Maps link above. Enter through the barrier and continue all the way past the hotel to the end.',
        'Park up and follow the red carpet.',
      ],
    },
    // "Staying over" is not here any more: it is a section guests act in rather
    // than read, so it lives in RoomSection with the live availability.
    {
      heading: 'What to wear',
      body: [
        'Our bridesmaids will be wearing deep red and olive green, so if you’d like to avoid those colours, feel free to, although there’s absolutely no pressure.',
        'We\'ve also heard through the grapevine that black isn’t appropriate for weddings, but seeing as it’s Jess’s favourite colour, we think that’s rubbish! Wear whatever you feel comfortable in. We’re just excited to spend our special day with you.',
      ],
    },
    {
      heading: 'Gifts',
      body: [
        'Your presence is present enough, but if you would like to give us a gift, we are putting together a honeymoon fund.',
      ],
    },
  ] satisfies InfoSection[],

  /*
   * A deadline is a deadline. None of these offer a way round one, because there
   * is not one to offer: the seats are counted and the kitchen is given numbers.
   * Saying "just ask" would only invite a conversation that has to end in no.
   */
  faqs: [
    {
      question: 'Can I bring my children?',
      answer:
        'Unfortunately, our guest list is jam packed! You would not believe the trouble we have had trying to fit everyone in.' +
        ' Due to limited space we are only able to accommodate children who are specifically named on the invitation.',
    },
    {
      question: 'I have a dietary requirement.',
      answer:
        'Tick it under Food, once you have told us you are coming - vegetarian, vegan and gluten free are all there. If yours is not one of those, tell us before ' +
        DIETARY_BY +
        ', which is when the kitchen needs to know.',
    },
    {
      question: 'What if my plans change?',
      answer:
        'Come back to this page any time and change your answer - your code keeps working. One thing to know: if you have told us you cannot make it, we may have offered your place to somebody else, so we cannot promise it will still be there. We need your answer by ' +
        RSVP_BY +
        '.',
    },
    {
      question: 'Can I change what I have told you about food?',
      answer:
        'Yes - use Change under Food, up until ' +
        DIETARY_BY +
        '. After that it is fixed: the kitchen will have the numbers and we cannot change them.',
    },
    {
      question: 'Accessibility',
      answer:
        'The manor house entrance has 7 steps, but a chair lift is available. The remainder of the house is on one level. Please let us know beforehand if you need the lift so we can make sure it is available for you. The manor house rooms are all upstairs and do not have disabled access. The rooms at Horsley Place do, so please book there instead if stairs are a problem.',
    },
  ] satisfies FaqItem[],

  contact: {
    label: 'Any questions at all',
    email: 'tomwright2@hotmail.co.uk',
    /** Written out for reading; the tel: link strips the spaces. */
    phone: '+44 7860 858093',
  },
} as const
